import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * 30-day Recovery Score trend (BW18 PR-E).
 *
 * Reads `daily_recovery` for the signed-in user, last 30 rows. Reference
 * lines at 50 (moderate) and 70 (quality) match the Target Exertion Zone
 * bands.
 */
export function RecoveryTrendChart() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery({
    queryKey: ["recovery_trend_30d", userId],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const since = new Date();
      since.setDate(since.getDate() - 30);
      const { data, error } = await supabase
        .from("daily_recovery")
        .select("date, score, ea_kcal_per_kg_ffm")
        .eq("user_id", userId)
        .gte("date", since.toISOString().slice(0, 10))
        .order("date", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!q.data || q.data.length === 0) {
    return (
      <section style={cardStyle()}>
        <h3 style={titleStyle()}>30-day Recovery trend</h3>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          Trend appears after a few days of computed scores.
        </p>
      </section>
    );
  }

  const data = q.data.map((d) => ({
    date: d.date.slice(5),
    score: d.score,
  }));

  return (
    <section style={cardStyle()}>
      <h3 style={titleStyle()}>30-day Recovery trend</h3>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--fg-3)" }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "var(--fg-3)" }} />
            <Tooltip
              contentStyle={{
                background: "var(--bg-2)",
                border: "1px solid var(--hair)",
                borderRadius: 8,
                fontSize: 12,
              }}
            />
            <ReferenceLine y={50} stroke="var(--hair-2)" strokeDasharray="2 4" />
            <ReferenceLine y={70} stroke="var(--hair-2)" strokeDasharray="2 4" />
            <Line type="monotone" dataKey="score" stroke="var(--accent)" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function cardStyle(): React.CSSProperties {
  return {
    background: "var(--bg-2)",
    border: "1px solid var(--hair)",
    borderRadius: "var(--radius-surface)",
    padding: "var(--space-5) var(--space-6)",
  };
}

function titleStyle(): React.CSSProperties {
  return {
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    fontSize: "var(--type-body)",
    margin: "0 0 var(--space-4)",
    color: "var(--fg)",
  };
}
